import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import type React from "react";
import { Formik, Form, FastField, FieldProps } from "formik";
import * as yup from "yup";
import { FormControl, FormHelperText, Box, Typography } from "@mui/material";
import { AppTextField } from "./text-field";
import { FormPrompt } from "./form-prompt";
import type { FormikHelpers } from "formik";
import { DateTime } from "luxon";

export interface DiaryFormValues {
  subject: string;
  enteredBy: string;
  tags: string;
  body: string;
  archived: boolean;
  createdAt?: DateTime;
}

export default function DiaryForm(
  props: React.PropsWithChildren<{
    initialValues: DiaryFormValues;
    saved: boolean;
    formId?: string;
    onSubmit: (
      values: DiaryFormValues,
      formikHelpers: FormikHelpers<DiaryFormValues>,
    ) => void | Promise<void>;
  }>,
) {
  const formId = props.formId ?? "diary-form";

  return (
    <Formik
      initialValues={props.initialValues}
      validationSchema={yup.object({
        subject: yup.string().required("Required"),
        enteredBy: yup.string().required("Required"),
        tags: yup.string(),
        body: yup.string().max(5000).required("Required"),
        archived: yup.boolean(),
      })}
      onSubmit={async (values, formikHelpers) => {
        await props.onSubmit(values, formikHelpers);
        formikHelpers.resetForm({ values });
      }}
    >
      {(form) => (
        <>
          <FormPrompt form={form} saved={props.saved} />
          <Form id={formId} noValidate>
            <Box display="flex" flexDirection="column" gap={2} maxWidth={600}>
              <FormControl
                variant="standard"
                error={form.touched.archived && Boolean(form.errors.archived)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="archived"
                      color="primary"
                      onChange={form.handleChange}
                      checked={form.values.archived}
                    />
                  }
                  label="Archived"
                />
                {form.touched.archived && form.errors.archived && (
                  <FormHelperText>{form.errors.archived}</FormHelperText>
                )}
              </FormControl>

              <FastField name="subject">
                {(fieldProps: FieldProps) => (
                  <AppTextField
                    {...fieldProps}
                    label="Subject"
                    inputProps={{ maxLength: 100 }}
                  />
                )}
              </FastField>

              <FastField name="enteredBy">
                {(fieldProps: FieldProps) => (
                  <AppTextField {...fieldProps} label="Entered By" />
                )}
              </FastField>

              <FastField name="tags">
                {(fieldProps: FieldProps) => (
                  <AppTextField {...fieldProps} label="Tags" />
                )}
              </FastField>

              <FastField name="body">
                {(fieldProps: FieldProps) => (
                  <AppTextField
                    {...fieldProps}
                    label="Body"
                    multiline
                    rows={6}
                    inputProps={{ maxLength: 5000 }}
                  />
                )}
              </FastField>

              <Typography variant="caption" align="right">
                {form.values.body.length}/5000 characters
              </Typography>
            </Box>

            {/* Spacer so last fields don't hide behind sticky action bar */}
            <Box sx={{ height: 110 }} />

            {/* StickyActionBar injected by parent */}
            {props.children}
          </Form>
        </>
      )}
    </Formik>
  );
}
